import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import HotRanking from '@/pages/home/components/HotRanking';
import SectionHeading from '@/components/feature/SectionHeading';
import { getHot, hotSearchTerms, searchArticles, sortByHot, sortByTime } from '@/utils/articles';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

type SortKey = 'relevance' | 'time' | 'hot';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [input, setInput] = useState(keyword);
  const [sort, setSort] = useState<SortKey>('relevance');

  useDocumentMeta({
    title: keyword ? `搜索“${keyword}” - 91吃瓜` : '搜索资讯 - 91吃瓜',
    description: '在 91吃瓜 搜索娱乐新闻、明星动态与网络热点，快速找到你想看的娱乐资讯。',
    keywords: '91吃瓜搜索,娱乐新闻搜索,明星动态,网络热点',
  });

  const results = useMemo(() => {
    const list = searchArticles(keyword);
    if (sort === 'time') return sortByTime(list);
    if (sort === 'hot') return sortByHot(list);
    return list;
  }, [keyword, sort]);

  const hotList = useMemo(() => getHot(5), []);

  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
    setSort('relevance');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">搜索</span>
      </nav>

      <header className="mt-5">
        <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">搜索资讯</h1>
        <p className="mt-2 text-sm text-foreground-500">输入关键词，匹配标题、简介与标签</p>
      </header>

      <form onSubmit={onSearch} className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-2xl">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="search"
            placeholder="搜索明星、剧集、热点关键词"
            className="w-full h-11 pl-11 pr-4 text-sm rounded-full border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <button
          type="submit"
          className="h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {keyword && (
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <p className="text-sm text-foreground-600">
            关键词：
            <strong className="text-foreground-950 font-semibold">{keyword}</strong>
            <span className="ml-2 text-foreground-400">共 {results.length} 条结果</span>
          </p>
          {results.length > 0 && (
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 ml-auto">
              {([
                { key: 'relevance', label: '相关度' },
                { key: 'time', label: '最新' },
                { key: 'hot', label: '最热' },
              ] as { key: SortKey; label: string }[]).map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2">
          {!keyword ? (
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h2 className="font-heading font-bold text-lg text-foreground-950">大家都在搜</h2>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {hotSearchTerms.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => {
                      setInput(term);
                      setSearchParams({ q: term });
                      setSort('relevance');
                    }}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-search-line mr-1 text-xs" />
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <div className="text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                  没有找到与“{keyword}”相关的内容
                </h2>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热搜推荐</p>
                <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setInput('');
                      setSearchParams({});
                    }}
                    className="px-4 py-2 rounded-md bg-background-100 text-foreground-800 border border-background-200 text-sm font-medium hover:border-primary-300 cursor-pointer whitespace-nowrap"
                  >
                    重置关键词
                  </button>
                  <Link
                    to="/"
                    className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-background-200">
                <h3 className="font-heading font-bold text-base text-foreground-950">热搜推荐</h3>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {hotSearchTerms.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => {
                        setInput(term);
                        setSearchParams({ q: term });
                      }}
                      className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {results.map((article) => (
                <ArticleCard key={article.id} article={article} variant="row" />
              ))}
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-8">
          <div>
            <SectionHeading title="热门内容榜" icon="ri-fire-line" />
            <HotRanking articles={hotList} />
          </div>
        </aside>
      </div>
    </div>
  );
}